// The public status page's logic (TOM-930, cloud ADR 0453): the API's shapes,
// the tone of each day, the uptime over the days shown, the banner and the
// calendar grid. Pure, so statusPage.test.ts covers it without a browser.

import type { Tone, UptimeBarDay } from '../components/charts';

export type ComponentId = 'website' | 'boat_connections' | 'neo' | 'alarm_notifications';
export type DayState = 'operational' | 'degraded' | 'outage' | 'maintenance' | 'no_data';
export type ComponentState = 'operational' | 'degraded' | 'down' | 'maintenance' | 'unknown';
export type OverallState =
  | 'operational'
  | 'degraded'
  | 'partial_outage'
  | 'major_outage'
  | 'maintenance'
  | 'unknown';
export type IncidentState =
  | 'investigating'
  | 'identified'
  | 'monitoring'
  | 'resolved'
  | 'scheduled'
  | 'in_progress'
  | 'completed';

export interface StatusDay {
  date: string;
  state: DayState;
  expectedMinutes: number;
  downMinutes: number;
  degradedMinutes: number;
  incidentIds: string[];
}

export interface StatusComponent {
  id: ComponentId;
  name: string;
  description: string;
  state: ComponentState;
  checkedAt: string | null;
  days: StatusDay[];
}

export interface IncidentUpdate {
  id?: string;
  state: IncidentState;
  body: string;
  createdAt: string;
}

export interface Incident {
  id: string;
  kind: 'incident' | 'maintenance';
  title: string;
  impact: 'minor' | 'major' | 'maintenance';
  state: IncidentState;
  components: ComponentId[];
  startsAt: string;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  latestUpdate: IncidentUpdate | null;
}

export interface IncidentWithUpdates extends Incident {
  updates: (IncidentUpdate & { id: string })[];
}

export interface StatusPage {
  generatedAt: string;
  monitoringSince: string | null;
  overall: { state: OverallState; label: string };
  components: StatusComponent[];
  activeIncidents: Incident[];
  upcomingMaintenance: Incident[];
  incidents: Incident[];
}

export interface BoatLinkDay {
  date: string;
  connectedMinutes: number;
  expectedMinutes: number;
}

export interface BoatLink {
  id: string;
  name: string;
  createdAt: string;
  lastContactedAt: string | null;
  days: BoatLinkDay[];
}

/** Below this width the bars show the last 30 days instead of 90. */
export const NARROW_BARS_PX = 600;
export const NARROW_DAYS = 30;

export const COMPONENT_NAMES: Record<ComponentId, string> = {
  website: 'Website and API',
  boat_connections: 'Boat connections',
  neo: 'Neo assistant',
  alarm_notifications: 'Alarm notifications',
};

export const DAY_STATE_LABELS: Record<DayState, string> = {
  operational: 'No downtime recorded',
  degraded: 'Degraded',
  outage: 'Outage',
  maintenance: 'Maintenance',
  no_data: 'No data',
};

export const COMPONENT_STATE_LABELS: Record<ComponentState, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  down: 'Outage',
  maintenance: 'Maintenance',
  unknown: 'No check in the last 3 minutes',
};

export const INCIDENT_STATE_LABELS: Record<IncidentState, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
};

export const IMPACT_LABELS: Record<Incident['impact'], string> = {
  minor: 'Minor',
  major: 'Major',
  maintenance: 'Maintenance',
};

/** A day's mark tone; null draws the bare track. */
export function dayTone(state: DayState): Tone | null {
  switch (state) {
    case 'operational':
      return 'good';
    case 'degraded':
      return 'warn';
    case 'outage':
      return 'bad';
    case 'maintenance':
      return 'neutral';
    case 'no_data':
      return null;
  }
}

/** The tone of a status word or chip: good, warn, bad, or muted. */
export type StatusTone = 'good' | 'warn' | 'bad' | 'muted';

export function componentTone(state: ComponentState): StatusTone {
  switch (state) {
    case 'operational':
      return 'good';
    case 'degraded':
      return 'warn';
    case 'down':
      return 'bad';
    case 'maintenance':
    case 'unknown':
      return 'muted';
  }
}

export function overallTone(state: OverallState): StatusTone {
  switch (state) {
    case 'operational':
      return 'good';
    case 'degraded':
    case 'partial_outage':
      return 'warn';
    case 'major_outage':
      return 'bad';
    case 'maintenance':
    case 'unknown':
      return 'muted';
  }
}

/** Uptime over the days given, in percent; null when no minute was expected. */
export function windowUptime(
  days: readonly Pick<StatusDay, 'expectedMinutes' | 'downMinutes'>[]
): number | null {
  const expected = days.reduce((n, d) => n + d.expectedMinutes, 0);
  if (expected === 0) return null;
  const down = days.reduce((n, d) => n + d.downMinutes, 0);
  return ((expected - down) / expected) * 100;
}

/**
 * A percentage to two places, rounded down so a day with any downtime never
 * reads 100.00%. Null reads as "No data".
 */
export function formatPercent(value: number | null): string {
  if (value === null) return 'No data';
  if (value >= 100) return '100%';
  return `${(Math.floor(value * 100) / 100).toFixed(2)}%`;
}

/** Minutes in words: "3 min", "1 h 5 min", "24 h". */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** "29 Oct 2026" for a UTC date string. */
export function formatDay(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The last `count` days, or all of them. */
export function lastDays<T>(days: readonly T[], count: number): T[] {
  return days.slice(Math.max(0, days.length - count));
}

/** One component day as an uptime bar. Incident links are added by the page. */
export function componentDayBar(day: StatusDay): Omit<UptimeBarDay, 'detail'> {
  return {
    key: day.date,
    tone: dayTone(day.state),
    quiet: day.state === 'operational',
    label: `${formatDay(day.date)} (UTC): ${DAY_STATE_LABELS[day.state]}`,
  };
}

export type BoatDayKind = 'all_day' | 'part' | 'none' | 'no_data';

/** How a boat's day reads: connected all day, part of it, not at all, or no data. */
export function boatDayKind(day: BoatLinkDay): BoatDayKind {
  if (day.expectedMinutes === 0) return 'no_data';
  if (day.connectedMinutes === 0) return 'none';
  return day.connectedMinutes >= day.expectedMinutes ? 'all_day' : 'part';
}

const BOAT_DAY_LABELS: Record<BoatDayKind, string> = {
  all_day: 'Connected all day',
  part: 'Connected part of the day',
  none: 'Not connected',
  no_data: 'No data',
};

/**
 * One boat day as an uptime bar, in neutral ink: a boat switched off is not an
 * outage, so no amber or red. The bar is as tall as the share connected.
 */
export function boatDayBar(day: BoatLinkDay): UptimeBarDay {
  const kind = boatDayKind(day);
  const label = `${formatDay(day.date)} (UTC): ${BOAT_DAY_LABELS[kind]}`;
  if (kind === 'no_data') return { key: day.date, tone: null, label };
  const share = day.connectedMinutes / day.expectedMinutes;
  return {
    key: day.date,
    tone: 'ink',
    level: kind === 'none' ? 0 : Math.max(0.15, share),
    quiet: kind === 'all_day',
    label,
    detail:
      kind === 'none'
        ? undefined
        : `${formatMinutes(day.connectedMinutes)} of ${formatMinutes(day.expectedMinutes)}`,
  };
}

/** Connected minutes over expected minutes; null when nothing was expected. */
export function boatConnectedPercent(days: readonly BoatLinkDay[]): number | null {
  const expected = days.reduce((n, d) => n + d.expectedMinutes, 0);
  if (expected === 0) return null;
  return (days.reduce((n, d) => n + d.connectedMinutes, 0) / expected) * 100;
}

/** "Connected 97.12% of the last 90 days", or the no-data line. */
export function boatConnectedLine(days: readonly BoatLinkDay[]): string {
  const pct = boatConnectedPercent(days);
  if (pct === null) return 'No connection history yet';
  return `Connected ${formatPercent(pct)} of the last ${days.length} days`;
}

/** Whether an incident touches a UTC date: it has started, and not ended before the day. */
export function incidentTouchesDate(incident: Incident, date: string, nowMs: number): boolean {
  const dayStart = Date.parse(`${date}T00:00:00Z`);
  const dayEnd = dayStart + 86_400_000;
  const start = Date.parse(incident.startsAt);
  if (start > nowMs || start >= dayEnd) return false;
  const end = incident.endsAt === null ? nowMs : Date.parse(incident.endsAt);
  return end >= dayStart;
}

export interface CalendarCell {
  /** YYYY-MM-DD, UTC. */
  date: string;
  /** Day of the month. */
  day: number;
  inMonth: boolean;
}

/** A month as weeks, Monday first, padded with the days around it. */
export function calendarWeeks(year: number, month: number): CalendarCell[][] {
  const first = Date.UTC(year, month - 1, 1);
  const lead = (new Date(first).getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const total = Math.ceil((lead + daysInMonth) / 7) * 7;
  const cells: CalendarCell[] = Array.from({ length: total }, (_, i) => {
    const d = new Date(first + (i - lead) * 86_400_000);
    return {
      date: d.toISOString().slice(0, 10),
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === month - 1,
    };
  });
  return Array.from({ length: total / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
}

/** "2026-10" for a year and month. */
export function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** The month before or after, as [year, month]. */
export function shiftMonth(year: number, month: number, by: number): [number, number] {
  const d = new Date(Date.UTC(year, month - 1 + by, 1));
  return [d.getUTCFullYear(), d.getUTCMonth() + 1];
}

/** "October 2026". */
export function formatMonth(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "29 Oct, 14:05 UTC". */
export function formatInstant(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  return `${date}, ${time} UTC`;
}
